<script setup lang="ts">
  import {
    TALK_THREAD_TAB_CHILD_DISP_MAX,
    TALK_THREAD_TAB_QUERY_MAX,
    TALK_USER_DATA_QUERY_MAX,
  } from "~/consts/TalkConfig";
  import type { LoginInfo } from "~/entities/LoginInfo";
  import type { PlanInfo } from "~/entities/PlanInfo";
  import { Talk } from "~/entities/Talk";
  import { TalkUserData } from "~/entities/TalkUserData";
  import type { TalkRoom } from "~/entities/TalkRoom";
  import type { TalkRoomUser } from "~/entities/TalkRoomUser";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";

  // TODO store があるので Organisms に移す
  const talkRoomStore = useTalkRoomStore();

  interface Props {
    planInfo: PlanInfo;
    loginInfo: LoginInfo;
    height: string;
    userDataName: string;
    userDataList: TalkUserData[];
    talkRooms: TalkRoom[];
    talkRoomUsers: Record<string, TalkRoomUser>;
    fetchFunc: (planId: string, userId: string, lastCreatedAt: Date | undefined, count: number) => Promise<number>;
    showThreads?: boolean;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "select", userData: TalkUserData): Promise<void>;
    (event: "update", userDataList: TalkUserData[]): Promise<void>;
  }
  const emit = defineEmits<Emits>();

  // 読み込み済みアクティブトークユーザーデータ一覧（表示用）
  const activeTalkUserDataList = ref<TalkUserData[]>([]);

  // 読み込み済みアクティブスレッド子トーク一覧（表示用）
  const activeThreadChildrenList = ref({} as Record<string, Talk[]>);

  // トークユーザーデータ・スレッド子トーク一覧表示反映
  watch(
    () => props.userDataList,
    async () => {
      // スレッドの場合はそのまま（親トークが削除されていることもあるため。BEでよしなに処理済みにつき、FEでチェック不要）
      // スレッド以外の場合は未削除のみに絞り込む
      const activeList = props.showThreads ? props.userDataList : props.userDataList.filter(m => !m.talk.isDeleted());

      // showThreads が true の場合のみスレッド子トークを表示
      if (props.showThreads) {
        const children: Record<string, Talk[]> = {};
        for (const data of activeList) {
          if (data.talk.threadComments > 0) {
            children[data.talk.talkId] = await talkRoomStore.getThreadChildTalks(
              props.planInfo.id,
              data.talk.talkRoomId,
              data.talk.talkId,
              TALK_THREAD_TAB_CHILD_DISP_MAX,
            );
          }
        }
        activeThreadChildrenList.value = children;
      } else {
        activeThreadChildrenList.value = {};
      }

      activeTalkUserDataList.value = activeList;

      emit("update", activeList);
    },
    // スレッドの場合は createdAt が更新されることがあるので、deep オプションが必要
    { deep: true },
  );

  // トークユーザーデータが空かどうか
  const isEmpty = ref(false);
  watch(activeTalkUserDataList, () => {
    isEmpty.value = activeTalkUserDataList.value.length === 0;
  });

  // 一度に読み込むトークユーザーデータ数
  // スレッドタブの場合はクエリが重い分、個数を減らす
  const PAGE_SIZE = props.showThreads ? TALK_THREAD_TAB_QUERY_MAX : TALK_USER_DATA_QUERY_MAX;

  const load = async ({ done }: any) => {
    const lastCreatedAt = props.userDataList.length
      ? props.userDataList[props.userDataList.length - 1].createdAt
      : undefined;
    try {
      const fetchedCount = await props.fetchFunc(props.planInfo.id, props.loginInfo.userId, lastCreatedAt, PAGE_SIZE);
      if (fetchedCount === PAGE_SIZE) {
        done("ok");
      } else {
        done("empty");
      }
    } catch (e: any) {
      consoleError(e);
      done("error");
    }
  };
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="talk-user-data-list">
    <div v-if="!loginInfo.userId" class="talk-user-data-message">ログインしていません</div>
    <div v-else-if="isEmpty" class="talk-user-data-message">{{ userDataName }}がありません</div>

    <v-infinite-scroll v-else class="talk-user-data-scroll" :height="height" @load="load">
      <template v-for="(data, index) in activeTalkUserDataList" :key="index">
        <MoleculesTalkUserDataItem
          :plan-info="planInfo"
          :talk-user-data="data"
          :height="height"
          :talk-rooms="props.talkRooms"
          :talk-room-users="props.talkRoomUsers"
          @select="emit('select', data)"
        >
          <!-- スレッドの子トーク表示 -->
          <template v-if="showThreads">
            <div
              v-if="data.talk.threadComments > TALK_THREAD_TAB_CHILD_DISP_MAX"
              class="ml-16 my-2 talk-user-data-thread-comments"
            >
              他 {{ data.talk.threadComments - TALK_THREAD_TAB_CHILD_DISP_MAX }} コメント
            </div>

            <MoleculesTalkUserDataItem
              v-for="(childTalk, childIndex) in activeThreadChildrenList[data.talk.talkId]"
              :key="childIndex"
              class="ml-8"
              :plan-info="planInfo"
              :talk-user-data="new TalkUserData(childTalk, childTalk.postDateTime!, false)"
              :height="height"
              :talk-rooms="props.talkRooms"
              :talk-room-users="props.talkRoomUsers"
              :simplify="true"
            />
          </template>
        </MoleculesTalkUserDataItem>
      </template>
      <template #empty>
        <div class=""></div>
      </template>
    </v-infinite-scroll>
  </div>
</template>

<style lang="scss" scoped>
  .talk-user-data-list {
    background-color: rgb(22, 22, 22);
    border-bottom: 1px solid rgb(var(--v-theme-monochrome-08));
  }
  .talk-user-data-message {
    padding: 16px;
    height: v-bind("height");
  }
  .talk-user-data-scroll {
    scrollbar-width: none;
  }
  .talk-user-data-thread-comments {
    color: rgb(var(--v-theme-primary));
    font-size: 12px;
  }
</style>
